import { useState, useEffect, useRef } from 'react'
import { t } from '@/utils/i18n'
import styles from './index.module.less'
import { utils, apis } from '@/utils'
import ProductCell from '../ProductCell'
import Masonry from 'react-masonry-css'
import { InfiniteScroll, DotLoading } from 'antd-mobile' // List
import useRefState from '@/hooks/useRefState'
import noDataImg from '@/assets/img/default/empty_list.jpg'
import type { ProductItem } from '@/types/product'

const { to, px2rem } = utils
const { productApi } = apis

interface IProps {
  searchText?: string
  corpId: string
  codeId?: string
  filter?: any
}

const Products = (props: IProps) => {
  const { searchText, corpId, codeId } = props

  const [productList, setProductList] = useState<ProductItem[]>([])
  const [pageLoading, setPageLoading] = useState(false)

  const refMasonry = useRef<HTMLDivElement | null>(null)
  const [cellWidth, setCellWidth] = useState('')
  const [hasMore, setHasMore] = useState(false)
  const [noData, setNoData] = useState(false)
  const [pageNum, setPageNum, pageNumRef] = useRefState(1)

  const breakpointColumnsObj = {
    default: 2,
  }

  useEffect(() => {
    setPageNum(1)
    getProductList()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText])

  async function getProductList() {
    setPageLoading(true)
    const params: any = {
      corpId,
      pageSize: 20,
      pageNum: pageNumRef.current,
      searchText,
    }

    if (codeId) {
      params.codeId = codeId
    } else {
      params.recommendType = 22 // 22表示30天内上新
    }

    // 搜图或者搜文本
    const [err, res] = await to(productApi.prodList(params))

    setPageLoading(false)

    if (err) {
      setProductList([])
      setNoData(true)
      setHasMore(false)
      return
    }

    const { data: list, pages } = res

    let totalList = []
    if (pageNumRef.current === 1) {
      totalList = list || []
    } else {
      totalList = productList.concat(...(list || []))
    }

    setNoData(!totalList.length)
    setProductList(totalList)

    setHasMore(pageNumRef.current < pages)

    return totalList
  }

  async function loadMore() {
    if (!hasMore) return
    setPageNum(pageNum + 1)
    await getProductList()
  }

  useEffect(() => {
    if (refMasonry.current) {
      const offset = window.getComputedStyle(refMasonry.current)

      setCellWidth(
        `calc(${window.screen.width / 2}px - ${offset.paddingLeft} - ${px2rem(
          8,
        )})`,
      )
    }
  }, [])

  return (
    <div className={styles['product-wrap']}>
      {(pageLoading || noData) && (
        <div className={styles['full-page-wrap']}>
          {pageLoading && (
            <DotLoading color="#FF5100" className={styles['dot-loading']} />
          )}
          {noData && (
            <div className={styles['no-data']}>
              <img src={noDataImg} alt="" className={styles['no-data-img']} />
              <div>{t('暂无商品2')}</div>
            </div>
          )}
        </div>
      )}

      <div className={styles['product-bd']} ref={refMasonry}>
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className={styles['my-masonry-grid']}
          columnClassName={styles['my-masonry-grid_column']}
        >
          {productList.map((item, index) => (
            <ProductCell
              item={item}
              index={index}
              corpId={corpId}
              key={index}
              cellWidth={cellWidth}
            />
          ))}
        </Masonry>

        {hasMore && (
          <InfiniteScroll
            threshold={350}
            loadMore={loadMore}
            hasMore={hasMore}
          />
        )}
      </div>
    </div>
  )
}

export default Products
